import SubHeaders from '@/components/Headers/SubHeaders'
import TabModules from '@/components/TabModules'
import { ModuleProps } from '@/components/TabModules/Module'
import useTabModules from '@/hooks/useTabModules'
import { paths } from '@/utils/path'
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import BusinessPerformanceTab from './components/BusinessPerformanceTab'
import BalanceSheetTab from './components/BalanceSheetTab'
import ProfitabilityTab from './components/ProfitabilityTab'
import FinancialStrengthTab from './components/FinancialStrengthTab'
import ManagementEfficiencyTab from './components/ManagementEfficiencyTab'
import OperatingCapabilityTab from './components/OperatingCapabilityTab'
import BusinessPerformanceIcons from '@/icons/BusinessPerformanceIcons'
import BalanceSheetIcons from '@/icons/BalanceSheetIcons'
import ProfitabilityIcons from '@/icons/ProfitabilityIcons'
import FinancialStrengthIcons from '@/icons/FinancialStrengthIcons'
import ManagementEfficiencyIcons from '@/icons/ManagementEfficiencyIcons'
import OperatingCapabilityIcons from '@/icons/OperatingCapabilityIcons'
import Filters from '../Revenue/components/shared/Filters'

const today = dayjs()

const BusinessPerformance = () => {
    const { t } = useTranslation()
    const [filters, setFilters] = useState<{
        gte: string
        lte: string
    }>({
        gte: today.startOf('year').format('YYYY-MM'),
        lte: today.endOf('year').format('YYYY-MM'),
    })

    const modules: ModuleProps[] = useMemo(
        () => [
            {
                icon: <BusinessPerformanceIcons className="text-primary" />,
                label: 'business_performance',
                value: '',
                component: <BusinessPerformanceTab filters={filters} />,
            },
            {
                icon: <BalanceSheetIcons className="text-success" />,
                label: 'balance_sheet',
                value: 'balance_sheet',
                component: <BalanceSheetTab filters={filters} />,
            },
            {
                icon: <ProfitabilityIcons className="text-warning" />,
                label: 'profitability',
                value: 'profitability',
                component: <ProfitabilityTab filters={filters} />,
            },
            {
                icon: <FinancialStrengthIcons className="text-info" />,
                label: 'financial_strength',
                value: 'financial_strength',
                component: <FinancialStrengthTab filters={filters} />,
            },
            {
                icon: <ManagementEfficiencyIcons className="text-purple-600" />,
                label: 'management_efficiency',
                value: 'management_efficiency',
                component: <ManagementEfficiencyTab filters={filters} />,
            },
            {
                icon: <OperatingCapabilityIcons className="text-indigo-600" />,
                label: 'operating_capability',
                value: 'operating_capability',
                component: <OperatingCapabilityTab filters={filters} />,
            },
        ],
        [filters]
    )

    const { TabProps, TabModuleComponent, activeTab } = useTabModules({
        modules,
    })

    const handleFiltersChange = (newFilters: { gte: string; lte: string }) => {
        setFilters(newFilters)
    }

    return (
        <>
            <SubHeaders
                breadcrumbs={[
                    {
                        path: paths?.reportBusinessPerformance,
                        title: t('report_business_performance'),
                    },
                    {
                        title: t(
                            activeTab === ''
                                ? 'business_performance'
                                : activeTab
                        ),
                        path: t(activeTab),
                    },
                ]}
            />
            <TabModules {...TabProps} gridCols={5} />
            <Filters
                excludeOptions={[
                    'sale_manager_codes',
                    'account_manager_codes',
                    'product_group_names',
                ]}
                onFiltersChange={handleFiltersChange}
                initialFilters={filters}
            />
            {TabModuleComponent}
        </>
    )
}

export default BusinessPerformance
